import { useCallback, useEffect, useState } from 'react';
import { Plus, Edit2, AlertTriangle, Clock, X, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

export type VisaStatus = 'active' | 'expired' | 'cancelled' | 'superseded';

export interface ClientVisa {
  id: string;
  contact_id: string;
  visa_subclass: string;
  variant: string | null;
  visa_grant_date: string | null;
  visa_expiry_date: string | null;
  status: VisaStatus;
  is_bridging: boolean;
  derived_from_matter_id: string | null;
  conditions: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface Props {
  contactId: string;
  /** Whether the current user is staff. Drives whether they can edit. */
  isStaff: boolean;
  /** Optional matter id to pre-fill `derived_from_matter_id` when adding a BVA. */
  currentMatterId?: string | null;
}

export default function ClientVisasPanel({ contactId, isStaff, currentMatterId }: Props) {
  const [visas, setVisas] = useState<ClientVisa[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ClientVisa | null>(null);
  const [adding, setAdding] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await supabase
      .from('client_visas')
      .select('*')
      .eq('contact_id', contactId)
      .order('status', { ascending: true })
      .order('visa_expiry_date', { ascending: true, nullsFirst: false });
    if (err) {
      setError(err.message);
    } else {
      setVisas((data ?? []) as ClientVisa[]);
      setError(null);
    }
    setLoading(false);
  }, [contactId]);

  useEffect(() => {
    if (!contactId) return;
    refresh();
  }, [contactId, refresh]);

  const active = visas.filter((v) => v.status === 'active');
  const inactive = visas.filter((v) => v.status !== 'active');

  return (
    <>
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-slate-100">
          <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
            Existing visa
          </h3>
          {isStaff && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="text-xs text-[#2CABEA] hover:underline inline-flex items-center gap-1"
            >
              <Plus size={11} /> Add
            </button>
          )}
        </div>

        <div className="p-3 space-y-3">
          {loading ? (
            <div className="text-xs text-slate-500 py-2">Loading visas...</div>
          ) : error ? (
            <div className="text-xs text-red-600">{error}</div>
          ) : active.length === 0 ? (
            <div className="text-xs text-slate-500">
              {isStaff ? 'No active visa on file. Add one if the client is currently in Australia or holds an offshore visa.' : 'No active visa on file.'}
            </div>
          ) : (
            active.map((v) => (
              <VisaRow
                key={v.id}
                visa={v}
                isStaff={isStaff}
                onEdit={() => setEditing(v)}
              />
            ))
          )}

          {inactive.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
                {inactive.length} historical visa{inactive.length === 1 ? '' : 's'}
              </summary>
              <div className="mt-2 space-y-2">
                {inactive.map((v) => (
                  <VisaRow
                    key={v.id}
                    visa={v}
                    isStaff={isStaff}
                    onEdit={() => setEditing(v)}
                    muted
                  />
                ))}
              </div>
            </details>
          )}
        </div>
      </div>

      {(editing || adding) && (
        <VisaEditModal
          contactId={contactId}
          visa={editing ?? undefined}
          currentMatterId={currentMatterId ?? null}
          onClose={() => {
            setEditing(null);
            setAdding(false);
          }}
          onSaved={() => {
            setEditing(null);
            setAdding(false);
            refresh();
          }}
        />
      )}
    </>
  );
}

function VisaRow({
  visa,
  isStaff,
  onEdit,
  muted = false,
}: {
  visa: ClientVisa;
  isStaff: boolean;
  onEdit: () => void;
  muted?: boolean;
}) {
  const severity = expirySeverity(visa.visa_expiry_date, visa.status);
  return (
    <div
      className={`flex items-start gap-2 ${muted ? 'opacity-60' : ''}`}
    >
      <SeverityDot severity={severity} />
      <div className="flex-1 min-w-0 text-sm">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-semibold text-[#0B2341]">
            {visa.visa_subclass}
            {visa.variant ? ` ${visa.variant}` : ''}
          </span>
          {visa.is_bridging && (
            <span className="text-[10px] uppercase tracking-wide font-semibold text-amber-700">
              Bridging
            </span>
          )}
          {visa.status !== 'active' && (
            <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">
              {visa.status}
            </span>
          )}
        </div>
        <div className="text-xs text-slate-600 mt-0.5">
          {visa.visa_expiry_date
            ? `Expires ${formatDate(visa.visa_expiry_date)}${expiryRelative(visa.visa_expiry_date, visa.status)}`
            : 'No expiry recorded'}
        </div>
        {visa.conditions && (
          <div className="text-xs text-slate-500 mt-0.5">Conditions: {visa.conditions}</div>
        )}
      </div>
      {isStaff && (
        <button
          type="button"
          onClick={onEdit}
          className="text-slate-400 hover:text-[#2CABEA] p-1"
          title="Edit"
        >
          <Edit2 size={12} />
        </button>
      )}
    </div>
  );
}

function SeverityDot({ severity }: { severity: 'red' | 'amber' | 'green' | 'grey' }) {
  if (severity === 'red')
    return <AlertTriangle size={14} className="text-red-600 mt-0.5 shrink-0" />;
  if (severity === 'amber')
    return <Clock size={14} className="text-amber-500 mt-0.5 shrink-0" />;
  if (severity === 'green')
    return <span className="w-2 h-2 mt-1.5 rounded-full bg-green-500 shrink-0" />;
  return <span className="w-2 h-2 mt-1.5 rounded-full bg-slate-300 shrink-0" />;
}

function expirySeverity(
  expiryISO: string | null,
  status: VisaStatus,
): 'red' | 'amber' | 'green' | 'grey' {
  if (status !== 'active') return 'grey';
  if (!expiryISO) return 'grey';
  const days = daysUntil(expiryISO);
  if (days < 7) return 'red';
  if (days < 30) return 'amber';
  return 'green';
}

function daysUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function expiryRelative(expiryISO: string, status: VisaStatus): string {
  if (status !== 'active' || !expiryISO) return '';
  const d = daysUntil(expiryISO);
  if (d < 0) return ` (expired ${Math.abs(d)}d ago)`;
  if (d === 0) return ' (today)';
  if (d < 31) return ` (in ${d}d)`;
  if (d < 365) return ` (in ~${Math.round(d / 30)}mo)`;
  return ` (in ~${Math.round(d / 365)}y)`;
}

// ============================================================
// Edit / add modal
// ============================================================

const VISA_SUBCLASS_OPTIONS = [
  '417 Working Holiday',
  '462 Work and Holiday',
  '482 Skills in Demand',
  '485 Temporary Graduate',
  '500 Student',
  '600 Visitor (Tourist)',
  '600 Visitor (Family Sponsored)',
  '676 Tourist',
  '820 Partner (onshore)',
  '801 Partner Stage 2',
  '309 Partner (offshore)',
  '100 Partner Stage 2',
  '300 Prospective Marriage',
  '461 NZ Family Relationship',
  '444 Special Category (NZ)',
  'BVA Bridging A',
  'BVB Bridging B',
  'BVC Bridging C',
  'BVD Bridging D',
  'BVE Bridging E',
  'Other',
];

function VisaEditModal({
  contactId,
  visa,
  currentMatterId,
  onClose,
  onSaved,
}: {
  contactId: string;
  visa?: ClientVisa;
  currentMatterId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = Boolean(visa);
  const [visaSubclass, setVisaSubclass] = useState(visa?.visa_subclass ?? '');
  const [variant, setVariant] = useState(visa?.variant ?? '');
  const [grantDate, setGrantDate] = useState(visa?.visa_grant_date ?? '');
  const [expiryDate, setExpiryDate] = useState(visa?.visa_expiry_date ?? '');
  const [status, setStatus] = useState<VisaStatus>(visa?.status ?? 'active');
  const [isBridging, setIsBridging] = useState(
    visa?.is_bridging ?? (visa?.visa_subclass?.toUpperCase().startsWith('BV') ?? false),
  );
  const [linkToCurrentMatter, setLinkToCurrentMatter] = useState(
    Boolean(visa?.derived_from_matter_id) || (isBridging && !!currentMatterId),
  );
  const [conditions, setConditions] = useState(visa?.conditions ?? '');
  const [notes, setNotes] = useState(visa?.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!visaSubclass.trim()) {
      setError('Visa subclass is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        contact_id: contactId,
        visa_subclass: visaSubclass.trim(),
        variant: variant.trim() || null,
        visa_grant_date: grantDate || null,
        visa_expiry_date: expiryDate || null,
        status,
        is_bridging: isBridging,
        derived_from_matter_id: linkToCurrentMatter ? currentMatterId : null,
        conditions: conditions.trim() || null,
        notes: notes.trim() || null,
      };
      if (isEdit && visa) {
        const { error: err } = await supabase
          .from('client_visas')
          .update(payload)
          .eq('id', visa.id);
        if (err) throw err;
      } else {
        const { error: err } = await supabase.from('client_visas').insert(payload);
        if (err) throw err;
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!visa) return;
    if (!confirm(`Remove this ${visa.visa_subclass} record? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const { error: err } = await supabase.from('client_visas').delete().eq('id', visa.id);
      if (err) throw err;
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h2 className="font-semibold text-[#0B2341]">
            {isEdit ? 'Edit visa record' : 'Add visa record'}
          </h2>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-slate-100">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="form-label">Visa subclass *</label>
            <input
              list="visa-subclass-options"
              type="text"
              value={visaSubclass}
              onChange={(e) => {
                setVisaSubclass(e.target.value);
                if (e.target.value.toUpperCase().startsWith('BV')) setIsBridging(true);
              }}
              className="form-input"
              placeholder="e.g. 500 Student"
            />
            <datalist id="visa-subclass-options">
              {VISA_SUBCLASS_OPTIONS.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
            <p className="form-hint">Type or pick from common options. Free-text accepted.</p>
          </div>

          <div>
            <label className="form-label">Variant or stream (optional)</label>
            <input
              type="text"
              value={variant}
              onChange={(e) => setVariant(e.target.value)}
              className="form-input"
              placeholder="e.g. de facto, family sponsored"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Grant date</label>
              <input
                type="date"
                value={grantDate}
                onChange={(e) => setGrantDate(e.target.value)}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Expiry date</label>
              <input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                className="form-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <label className="form-label">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as VisaStatus)}
                className="form-select"
              >
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
                <option value="superseded">Superseded</option>
              </select>
            </div>
            <label className="inline-flex items-center gap-2 text-sm pb-2">
              <input
                type="checkbox"
                checked={isBridging}
                onChange={(e) => setIsBridging(e.target.checked)}
              />
              Bridging visa
            </label>
          </div>

          {isBridging && currentMatterId && (
            <label className="inline-flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={linkToCurrentMatter}
                onChange={(e) => setLinkToCurrentMatter(e.target.checked)}
                className="mt-0.5"
              />
              <span>Link to this matter (record that this BV was triggered by the current application)</span>
            </label>
          )}

          <div>
            <label className="form-label">Conditions</label>
            <input
              type="text"
              value={conditions}
              onChange={(e) => setConditions(e.target.value)}
              className="form-input"
              placeholder="e.g. No work; 8503; work limited 40h/fortnight"
            />
          </div>

          <div>
            <label className="form-label">Internal notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="form-textarea"
              placeholder="Anything else the team should know"
            />
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-slate-200 bg-slate-50">
          {isEdit ? (
            <button
              type="button"
              onClick={handleDelete}
              disabled={saving}
              className="text-xs text-red-600 hover:underline disabled:opacity-40"
            >
              Delete
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="btn-secondary !py-1.5 text-sm">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="btn-primary !py-1.5 text-sm inline-flex items-center gap-1.5"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : null}
              {isEdit ? 'Save changes' : 'Add visa'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
